"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

export const slackToolRegistry = {
  slackListChannels: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackListChannelsToolView }) => ({
        default: SlackListChannelsToolView,
      }))
    ),
  },
  slackSearchChannels: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackSearchChannelsToolView }) => ({
        default: SlackSearchChannelsToolView,
      }))
    ),
  },
  slackReadMessages: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackReadMessagesToolView }) => ({
        default: SlackReadMessagesToolView,
      }))
    ),
  },
  slackReadThread: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackReadThreadToolView }) => ({
        default: SlackReadThreadToolView,
      }))
    ),
  },
  slackSendMessage: {
    kind: "step",
    category: "message",
    view: lazy(() =>
      import("./views").then(({ SlackSendMessageToolView }) => ({
        default: SlackSendMessageToolView,
      }))
    ),
  },
  slackGetUser: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackGetUserToolView }) => ({
        default: SlackGetUserToolView,
      }))
    ),
  },
  slackSearchUsers: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackSearchUsersToolView }) => ({
        default: SlackSearchUsersToolView,
      }))
    ),
  },
  slackCreateCanvas: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ SlackCreateCanvasToolView }) => ({
        default: SlackCreateCanvasToolView,
      }))
    ),
  },
  slackReadCanvas: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackReadCanvasToolView }) => ({
        default: SlackReadCanvasToolView,
      }))
    ),
  },
  slackGetPins: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackGetPinsToolView }) => ({
        default: SlackGetPinsToolView,
      }))
    ),
  },
  slackListFiles: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackListFilesToolView }) => ({
        default: SlackListFilesToolView,
      }))
    ),
  },
  slackUploadFile: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ SlackUploadFileToolView }) => ({
        default: SlackUploadFileToolView,
      }))
    ),
  },
  slackAddReaction: {
    kind: "step",
    category: "message",
    view: lazy(() =>
      import("./views").then(({ SlackAddReactionToolView }) => ({
        default: SlackAddReactionToolView,
      }))
    ),
  },
  slackGetReactions: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackGetReactionsToolView }) => ({
        default: SlackGetReactionsToolView,
      }))
    ),
  },
  slackSearchMessages: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ SlackSearchMessagesToolView }) => ({
        default: SlackSearchMessagesToolView,
      }))
    ),
  },
  slackShareOutput: {
    kind: "step",
    category: "message",
    view: lazy(() =>
      import("./views").then(({ SlackShareOutputToolView }) => ({
        default: SlackShareOutputToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant of `slackToolRegistry` keyed by the progressive-disclosure
 * inner-tool name `slack_${toolName}`.
 *
 * Slack is a progressive-disclosure capability (ADR-057), so its tool calls are
 * dispatched through the `callCapabilityTool` proxy and every durable +
 * live-stream tool part is rewritten to the namespaced inner identity
 * `${capabilityId}_${toolName}` (e.g. `slack_slackSendMessage`). The bare-name
 * entries above stay in the static tier (still required by `AllToolNames` and
 * used by legacy persisted chats); these namespaced entries reuse the same lazy
 * views so the rich Slack cards render on the PD path instead of falling through
 * to the generic JSON fallback. Wired into the built-in registry in
 * `packages/sdk/react/src/tools/built-in-registry.ts`.
 */
export const slackProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(slackToolRegistry).map(([name, meta]) => [
    `slack_${name}`,
    meta,
  ])
);
